"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PriceCalculator = void 0;
const currencies_1 = require("../consts/currencies");
const currency_enum_1 = require("../models/common/currency.enum");
const common_1 = require("./common");
class PriceCalculator {
    constructor(currency) {
        var _a, _b;
        this.precision = ((_a = currencies_1.Currencies[currency]) === null || _a === void 0 ? void 0 : _a.decimal_digits) || ((_b = currencies_1.Currencies[currency_enum_1.CurrencyEnum.EUR]) === null || _b === void 0 ? void 0 : _b.decimal_digits);
    }
    toRoundedAmount(dinero) {
        return dinero.toRoundedUnit(this.precision);
    }
    totalsFromUnitPriceAndQuantity(input) {
        const { unitPrice, quantity, taxRate = 0 } = input;
        const totalNetPriceDinero = (0, common_1.toDinero)(unitPrice).multiply(quantity);
        const totalTaxPriceDinero = totalNetPriceDinero.multiply(taxRate / 100);
        const totalGrossPriceDinero = totalNetPriceDinero.add(totalTaxPriceDinero);
        return {
            totalNetPrice: this.toRoundedAmount(totalNetPriceDinero),
            totalTaxPrice: this.toRoundedAmount(totalTaxPriceDinero),
            totalGrossPrice: this.toRoundedAmount(totalGrossPriceDinero),
        };
    }
    totalsFromGrossPrice(input) {
        const { totalGrossPrice, taxRate = 0 } = input;
        const totalGrossPriceDinero = (0, common_1.toDinero)(totalGrossPrice);
        const totalNetPriceDinero = totalGrossPriceDinero.divide(1 + taxRate / 100);
        const totalTaxPriceDinero = totalGrossPriceDinero.subtract(totalNetPriceDinero);
        return {
            totalNetPrice: this.toRoundedAmount(totalNetPriceDinero),
            totalTaxPrice: this.toRoundedAmount(totalTaxPriceDinero),
            totalGrossPrice: this.toRoundedAmount(totalGrossPriceDinero),
        };
    }
    totalsFromNetPrice(input) {
        const { totalNetPrice, taxRate = 0 } = input;
        const totalNetPriceDinero = (0, common_1.toDinero)(totalNetPrice);
        const totalTaxPriceDinero = totalNetPriceDinero.multiply(taxRate / 100);
        const totalGrossPriceDinero = totalNetPriceDinero.add(totalTaxPriceDinero);
        return {
            totalNetPrice: this.toRoundedAmount(totalNetPriceDinero),
            totalTaxPrice: this.toRoundedAmount(totalTaxPriceDinero),
            totalGrossPrice: this.toRoundedAmount(totalGrossPriceDinero),
        };
    }
    totalsFromNetPriceAndTaxPrice(input) {
        const { totalNetPrice, totalTaxPrice } = input;
        const totalNetPriceDinero = (0, common_1.toDinero)(totalNetPrice);
        const totalTaxPriceDinero = (0, common_1.toDinero)(totalTaxPrice);
        const totalGrossPriceDinero = totalNetPriceDinero.add(totalTaxPriceDinero);
        return {
            totalNetPrice: this.toRoundedAmount(totalNetPriceDinero),
            totalTaxPrice: this.toRoundedAmount(totalTaxPriceDinero),
            totalGrossPrice: this.toRoundedAmount(totalGrossPriceDinero),
        };
    }
    taxPriceFromGrossAndNetPrice(input) {
        const { totalGrossPrice, totalNetPrice } = input;
        const totalGrossPriceDinero = (0, common_1.toDinero)(totalGrossPrice);
        const totalNetPriceDinero = (0, common_1.toDinero)(totalNetPrice);
        const totalTaxPriceDinero = totalGrossPriceDinero.subtract(totalNetPriceDinero);
        return {
            totalTaxPrice: this.toRoundedAmount(totalTaxPriceDinero),
        };
    }
    unitPriceFromNetPriceAndQuantity(input) {
        const { totalNetPrice, quantity } = input;
        if (quantity === 0) {
            if (totalNetPrice !== 0) {
                throw new Error(`IllegalArgument: quantity=${quantity} and totalNetPrice=${totalNetPrice}. Cannot divide by zero.`);
            }
            // if quantity is 0 and totalNetPrice is 0, return 0
            return {
                unitPrice: 0,
            };
        }
        const unitPriceDinero = (0, common_1.toDinero)(totalNetPrice).divide(quantity);
        return {
            unitPrice: this.toRoundedAmount(unitPriceDinero),
        };
    }
    priceInference(input) {
        const { unitPrice, quantity, taxRate, totalPrice } = input;
        // Scenario 1: unitPrice and quantity provided
        if (unitPrice != null && quantity != null) {
            const { totalNetPrice, totalTaxPrice, totalGrossPrice } = this.totalsFromUnitPriceAndQuantity({
                unitPrice,
                quantity,
                taxRate: taxRate !== null && taxRate !== void 0 ? taxRate : 0,
            });
            return {
                unitPrice: this.toRoundedAmount((0, common_1.toDinero)(unitPrice)),
                totalNetPrice,
                totalTaxPrice,
                totalGrossPrice,
            };
        }
        // Scenario 2: totalPrice provided
        if (totalPrice != null) {
            const isItemTotalGross = taxRate != null && taxRate > 0;
            if (isItemTotalGross) {
                const { totalNetPrice, totalTaxPrice, totalGrossPrice } = this.totalsFromGrossPrice({
                    totalGrossPrice: totalPrice,
                    taxRate: taxRate !== null && taxRate !== void 0 ? taxRate : 0,
                });
                let calculatedUnitPrice;
                if (quantity != null) {
                    const { unitPrice: unitPriceResult } = this.unitPriceFromNetPriceAndQuantity({
                        totalNetPrice,
                        quantity,
                    });
                    calculatedUnitPrice = unitPriceResult;
                }
                return {
                    unitPrice: calculatedUnitPrice,
                    totalNetPrice,
                    totalTaxPrice,
                    totalGrossPrice,
                };
            }
            // totalPrice is totalNetPrice
            const { totalNetPrice, totalTaxPrice, totalGrossPrice } = this.totalsFromNetPrice({
                totalNetPrice: totalPrice,
                taxRate: taxRate !== null && taxRate !== void 0 ? taxRate : 0,
            });
            let calculatedUnitPrice;
            if (quantity != null) {
                const { unitPrice: unitPriceResult } = this.unitPriceFromNetPriceAndQuantity({
                    totalNetPrice,
                    quantity,
                });
                calculatedUnitPrice = unitPriceResult;
            }
            return {
                unitPrice: calculatedUnitPrice,
                totalNetPrice,
                totalTaxPrice,
                totalGrossPrice,
            };
        }
        // Scenario 3: fallback - no valid price data
        return {
            unitPrice: unitPrice != null ? this.toRoundedAmount((0, common_1.toDinero)(unitPrice)) : undefined,
            totalNetPrice: this.toRoundedAmount((0, common_1.toDinero)(totalPrice !== null && totalPrice !== void 0 ? totalPrice : 0)),
            totalTaxPrice: 0,
            totalGrossPrice: this.toRoundedAmount((0, common_1.toDinero)(totalPrice !== null && totalPrice !== void 0 ? totalPrice : 0)),
        };
    }
}
exports.PriceCalculator = PriceCalculator;
